/**
 * The formats a customer can actually open, with a sentence each.
 *
 * The outputs table lists *artifacts* - what a pipeline stage wrote - and is
 * right to. This section lists **delivery formats**, which is a different
 * question: what somebody takes into Blender, or a slicer, or a viewer. STL
 * and the OBJ package are here and are not artifacts, because they are
 * derived from the stored model when asked for.
 *
 * Three rules the section keeps:
 *
 * **Only what exists is listed.** The server decides which formats this run
 * can deliver, from the artifacts it actually has. A run that never produced
 * a model shows no model formats rather than five disabled rows.
 *
 * **One recommendation, and it says why.** GLB carries geometry and
 * appearance in a single file, so it is the recommendation - and when the
 * model has no texture the label says so, because "3D model" that silently
 * means "grey 3D model" is a surprise at the wrong moment.
 *
 * **No claim a format cannot keep.** STL carries no texture and no unit, and
 * the copy says both. Nothing here says "ready to print": print readiness is a
 * separate measurement with its own panel, and a scale - even a verified one -
 * is not a print guarantee.
 *
 * ## Two things it used to get wrong
 *
 * **The size.** A stored format's size is shown, because somebody choosing
 * what to pull onto a phone is choosing partly on that. A *derived* format has
 * no size, and none is invented: an STL of the same mesh differs from its GLB
 * by a factor nobody can predict from a vertex count.
 *
 * **The scale sentence.** It said flatly that these models carry no real-world
 * size, which stopped being true when the measurement panel shipped and went
 * on being printed to customers who had just calibrated their own model.
 */

import { useCallback, useState } from "react";
import {
  getReconstructionBundle,
  getReconstructionExport,
  listReconstructionExports,
} from "@/api/client";
import type { FormatOffer, ScaleCalibration } from "@/api/types";
import { Button, Callout, Loading, StatusBadge } from "@/components/ui";
import { useAsync } from "@/lib/useAsync";
import { downloadFailureText } from "./artifactDownload";

/** The extension each format arrives with, for the saved filename. */
const SUFFIX: Record<FormatOffer["format"], string> = {
  glb: "glb",
  obj: "zip",
  stl: "stl",
  "3mf": "3mf",
  usdz: "usdz",
  ply: "ply",
  colmap: "txt",
};

/**
 * Hand bytes to the browser without ever putting the endpoint in the document.
 *
 * Same rule as `downloadArtifact`: a blob URL revoked immediately, never an
 * `<a href>` into the API, because a link is a URL somebody can copy out of
 * the page.
 */
function save(buffer: ArrayBuffer, filename: string): void {
  const url = URL.createObjectURL(new Blob([buffer]));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Bytes as a customer reads them. Null stays null - see the module note. */
function fileSize(bytes: number | null | undefined): string | null {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1e6).toFixed(1)} MB`;
}

export function ModelDownloads({
  jobId,
  version,
  scale,
}: {
  jobId: string;
  /** Only used to name the saved file, never to build a request. */
  version: number;
  /**
   * This job's stored calibration, or null when there is none or it has not
   * been read. Null is treated as uncalibrated, which is the safe direction:
   * the wrong way round would promise a real-world size the file has not got.
   */
  scale?: ScaleCalibration | null;
}) {
  const fetcher = useCallback(
    (signal: AbortSignal) => listReconstructionExports(jobId, { signal }),
    [jobId],
  );
  const { state } = useAsync(fetcher);
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  //: What to try again. A download that failed on a flaky connection is one
  //: press away from working, and re-choosing the format from a list of seven
  //: is a worse way to spend that press.
  const [retry, setRetry] = useState<null | { kind: "format"; offer: FormatOffer } | { kind: "bundle" }>(
    null,
  );

  const offers = state.data ?? [];
  // Stored formats keep their existing row in the artifacts table; this
  // section is about the ones a person chooses between.
  const derived = offers.filter((offer) => offer.derived);

  async function take(offer: FormatOffer) {
    if (busy) return;
    setBusy(offer.format);
    setFailed(null);
    setRetry(null);
    try {
      const bytes = await getReconstructionExport(jobId, offer.format);
      save(bytes, `sentycap-model-v${version}.${SUFFIX[offer.format]}`);
    } catch (error) {
      setFailed(downloadFailureText(error));
      setRetry({ kind: "format", offer });
    } finally {
      setBusy(null);
    }
  }

  async function takeEverything() {
    if (busy) return;
    setBusy("bundle");
    setFailed(null);
    setRetry(null);
    try {
      save(
        await getReconstructionBundle(jobId),
        `sentycap-ciktilar-v${version}.zip`,
      );
    } catch (error) {
      setFailed(downloadFailureText(error));
      setRetry({ kind: "bundle" });
    } finally {
      setBusy(null);
    }
  }

  if (state.status === "loading" && state.data === null) {
    return <Loading label="İndirme biçimleri yükleniyor" />;
  }
  if (offers.length === 0) return null;

  return (
    <div className="stack-tight">
      <ul className="formatlist">
        {offers.map((offer) => (
          <li key={offer.format} className="formatlist__row">
            <div className="formatlist__text">
              <p className="formatlist__label">
                {offer.label}
                {offer.recommended && (
                  <StatusBadge tone="success">Önerilen</StatusBadge>
                )}
              </p>
              <p className="muted small">
                {offer.description}
                {/* Where there is a stored file, how big it is. Nothing for a
                    derived format, because there is no file to measure. */}
                {fileSize(offer.byte_size) && (
                  <> · {fileSize(offer.byte_size)}</>
                )}
              </p>
              {/* A fact about the format, stated where the choice is made
                  rather than discovered after opening the file. */}
              {/*
                One line per format that has something a customer would
                otherwise find out by opening the file. Not a footnote about
                every format - only where the surprise would be real.
              */}
              {offer.format === "stl" && (
                <p className="faint">
                  Doku ve renk taşımaz; yalnızca geometri. Ölçü birimi de
                  taşımaz — dilimleyicide boyutu kontrol edin.
                </p>
              )}
              {offer.format === "3mf" && (
                <p className="faint">
                  Ölçek bu projede doğrulanmadığı için dosya “ölçek
                  doğrulanmadı” bilgisiyle yazılır.
                </p>
              )}
              {offer.format === "usdz" && (
                <p className="faint">
                  Apple cihazlarda önizleme için. Apple’ın kendi doğrulama
                  aracıyla test edilmedi.
                </p>
              )}
            </div>
            <div className="formatlist__action">
              {offer.derived ? (
                <Button
                  variant="secondary"
                  busy={busy === offer.format}
                  onClick={() => void take(offer)}
                  ariaLabel={`${offer.label} indir`}
                >
                  İndir
                </Button>
              ) : (
                <span className="faint">Aşağıdaki listede</span>
              )}
            </div>
          </li>
        ))}
      </ul>

      {derived.length > 0 && (
        <p className="faint">
          İşaretli biçimler indirme sırasında modelden üretilir; birkaç saniye
          sürebilir.
        </p>
      )}

      <div className="row">
        <Button
          variant="secondary"
          busy={busy === "bundle"}
          onClick={() => void takeEverything()}
        >
          Hepsini indir (ZIP)
        </Button>
        <span className="faint">
          Bu projenin çıktı dosyaları tek arşivde. Orijinal fotoğraflarınız ve
          motor günlüğü bu arşive dâhil değildir.
        </span>
      </div>

      {failed && (
        <Callout tone="danger">
          <div className="stack-tight">
            <span>{failed}</span>
            {/* One press. Nothing was written, so retrying is safe and is
                what the reader wants next. */}
            {retry && (
              <div>
                <Button
                  variant="secondary"
                  disabled={busy !== null}
                  onClick={() =>
                    void (retry.kind === "bundle"
                      ? takeEverything()
                      : take(retry.offer))
                  }
                >
                  Tekrar Dene
                </Button>
              </div>
            )}
          </div>
        </Callout>
      )}

      {/* Scale and print readiness are different claims. This says which of
          them is true for *this* model rather than for photogrammetry. */}
      <p className="faint">
        {scale?.verified
          ? "Bu modelin ölçeği doğrulandı; dosyalar gerçek ölçüde dışa aktarılır. Ölçeğin doğrulanmış olması, modelin baskıya hazır olduğu anlamına gelmez."
          : "Ölçek doğrulanmadı: bu modeller rekonstrüksiyon uzayındadır ve gerçek dünya ölçüsü içermez. 1:1 baskı için modeli kendi ölçünüze göre ölçeklemeniz gerekir."}
      </p>
    </div>
  );
}
